import type { Coordinate, Game } from './types';

/*
 * The contract's `prediction` object, as the two props `SquaresGrid` actually takes.
 *
 * One helper rather than the same conditional written out on the player's screen, the host's and
 * local play — three copies of a four-state check is three chances for one of them to drift, and
 * the states are not obvious enough to trust to copy-paste:
 *
 *   absent / null  never asked for. The switch is off. The grid says nothing.
 *   PENDING        asked, no answer yet. Words, no frame.
 *   READY          asked and answered. The frame.
 *   NONE           asked, and there is no answer. Same as absent on screen, counted separately.
 */

/** The square to frame, or null. Only ever non-null for a `READY` prediction with a square. */
export function predictedSquare(game: Game | null | undefined): Coordinate | null {
  const prediction = game?.prediction;
  if (prediction?.status !== 'READY') return null;

  const { col, row } = prediction;
  // The contract makes these optional because they are absent unless READY. Belt and braces: a
  // square at (undefined, undefined) would frame nothing while claiming to frame something.
  if (typeof col !== 'number' || typeof row !== 'number') return null;

  return { col, row };
}

/** Whether to say, in words, that the answer is still coming. */
export const isPredicting = (game: Game | null | undefined): boolean =>
  game?.prediction?.status === 'PENDING';
